import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { ContentAssetRow } from "./types";

export type ToggleAssetArchiveInput = {
  id: string;
  archived: boolean;
};

type Context = {
  previousList: ContentAssetRow[] | undefined;
  previousDetail: ContentAssetRow | undefined;
};

// PATCH /api/admin/content/assets/:id/archived
//
// optimistic: archive/restore is a single-click flip — patch archived_at in
// place so the row's badge swaps immediately, defer DB confirmation. One
// endpoint covers both directions (server picks archiveAsset vs restoreAsset
// based on `archived`).
//
// Pattern C (state-flip) — see MIGRATION.md "Optimistic-update reference".
// Mirrors apps/web/src/features/portal/use-toggle-partner-task.ts.
export function useToggleAssetArchive() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, ToggleAssetArchiveInput, Context>({
    mutationFn: ({ id, archived }) =>
      apiFetch<{ ok: true }>(`/admin/content/assets/${id}/archived`, {
        method: "PATCH",
        body: JSON.stringify({ archived }),
      }),
    onMutate: async ({ id, archived }) => {
      const listKey = queryKeys.content.list();
      const detailKey = queryKeys.content.detail(id);
      await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({ queryKey: detailKey });

      const previousList = queryClient.getQueryData<ContentAssetRow[]>(listKey);
      const previousDetail = queryClient.getQueryData<ContentAssetRow>(detailKey);

      const nextArchivedAt = archived ? new Date().toISOString() : null;
      queryClient.setQueryData<ContentAssetRow[]>(listKey, (old) =>
        old?.map((row) =>
          row.id === id ? { ...row, archived_at: nextArchivedAt } : row,
        ),
      );
      queryClient.setQueryData<ContentAssetRow>(detailKey, (old) =>
        old ? { ...old, archived_at: nextArchivedAt } : old,
      );

      return { previousList, previousDetail };
    },
    onError: (err, { id }, context) => {
      if (context?.previousList !== undefined) {
        queryClient.setQueryData(queryKeys.content.list(), context.previousList);
      }
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(queryKeys.content.detail(id), context.previousDetail);
      }
      toast.error(err.message ?? "Failed to update asset");
    },
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.content.detail(id) });
      // Archiving a folder removes it from the active-folders picker;
      // restoring it puts it back. Conservative invalidate — we don't
      // know the asset's is_folder flag from the input shape.
      queryClient.invalidateQueries({ queryKey: queryKeys.content.folders() });
    },
  });
}
